import React, { useCallback, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { useGoogleTranslate } from '../../../Hooks/useGoogleTranslate';
import CustomButton from '../../../Components/CustomButton';
import AffiliateBankAccountModal from '../../../Components/AffiliateBankAccountModal/AffiliateBankAccountModal';
import SkeletonLoader from '../../../Components/SkeletonLoader/SkeletonLoader';
import {
  getAffiliateDashboard,
  getAffiliateBankAccount,
  joinAffiliate,
} from '../../../Services/User/Affiliate';
import { showToast } from '../../../Components/Toast/toast';
import heartDecor from '../../../assets/images/coin-store-figma/decor-heart.svg';
import './AffiliateProgram.css';

const formatUsd = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return value;
};

const displayMaskedAccountNumber = (bankAccount) =>
  formatDisplayValue(bankAccount?.account_number_masked);

const AffiliateProgram = () => {
  const pageTitle = useGoogleTranslate('Affiliate Program');
  const copyLabel = useGoogleTranslate('Copy');
  const sharePrefix = useGoogleTranslate('Use my code');
  const shareSuffix = useGoogleTranslate('when signing up');
  const queryClient = useQueryClient();
  const [showBankModal, setShowBankModal] = useState(false);

  usePageTitle(pageTitle);

  const loadDashboard = useCallback(async () => {
    try {
      return await getAffiliateDashboard();
    } catch (error) {
      if (error.status === 403 || error.message?.toLowerCase().includes('affiliate')) {
        await joinAffiliate();
        return getAffiliateDashboard();
      }
      throw error;
    }
  }, []);

  const {
    data: dashboard,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['userAffiliateDashboard'],
    queryFn: loadDashboard,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const { data: bankAccount, isLoading: isBankAccountLoading } = useQuery({
    queryKey: ['userAffiliateBankAccount'],
    queryFn: getAffiliateBankAccount,
    enabled: !!dashboard?.is_affiliate && !!dashboard?.has_bank_account,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleCopyCode = () => {
    const code = dashboard?.affiliate_code;
    if (!code) return;
    navigator.clipboard
      .writeText(code)
      .then(() => showToast('Affiliate code copied to clipboard!', 'success'))
      .catch(() => showToast('Failed to copy affiliate code', 'error'));
  };

  const handleBankSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['userAffiliateDashboard'] });
    queryClient.invalidateQueries({ queryKey: ['userAffiliateBankAccount'] });
  };

  const stats = dashboard?.stats || {};

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (isError) {
    return (
      <section className="affiliate-program-page">
        <Container className="py-5 text-center">
          <p className="text-danger">{error?.message || 'Failed to load affiliate dashboard'}</p>
        </Container>
      </section>
    );
  }

  return (
    <section className="affiliate-program-page">
      <img src={heartDecor} alt="" className="affiliate-program-heart" aria-hidden="true" />
      <div className="affiliate-program-hero">
        <Container>
          <h1 className="affiliate-program-title">{pageTitle}</h1>
        </Container>
      </div>
      <div className="affiliate-program-main py-sm-5 py-4">
        <Container className="affiliate-program-container">
          <Row className="justify-content-center mb-4">
            <Col xs={12} lg={8}>
              <div className="affiliate-code-card">
                <label className="site-label">Your Affiliate Code</label>
                <div className="affiliate-code-row">
                  <input type="text" value={dashboard?.affiliate_code || ''} readOnly className="affiliate-code-input" />
                  <CustomButton onClick={handleCopyCode} className="affiliate-copy-btn">
                    {copyLabel}
                  </CustomButton>
                </div>
                <p className="affiliate-share-text mb-0">
                  {sharePrefix} <strong>{dashboard?.affiliate_code}</strong> {shareSuffix}
                </p>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            {[
              { label: 'Total Signups', value: stats.signups_count ?? 0, isCurrency: false },
              { label: 'Subscribed Users', value: stats.subscribed_count ?? 0, isCurrency: false },
              { label: 'Total Earned', value: stats.total_earned ?? 0, isCurrency: true },
              { label: 'Pending Payout', value: stats.pending_payout ?? 0, isCurrency: true },
              { label: 'Total Paid Out', value: stats.total_paid_out ?? 0, isCurrency: true },
            ].map((item) => (
              <Col xs={12} sm={6} lg={4} key={item.label}>
                <div className="affiliate-stat-card">
                  <p className="affiliate-stat-label">{item.label}</p>
                  <p className="affiliate-stat-value">
                    {item.isCurrency ? formatUsd(item.value) : item.value}
                  </p>
                </div>
              </Col>
            ))}
          </Row>

          <Row className="justify-content-center">
            <Col xs={12} lg={8}>
              <div className="affiliate-bank-card">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">Bank Account</h5>
                    <p className="mb-0 text-muted">
                      {dashboard?.has_bank_account
                        ? 'Your saved bank details for affiliate payouts.'
                        : 'Add your bank details to receive affiliate payouts.'}
                    </p>
                  </div>
                  <CustomButton onClick={() => setShowBankModal(true)}>
                    {dashboard?.has_bank_account ? 'Update Bank Account' : 'Add Bank Account'}
                  </CustomButton>
                </div>

                {dashboard?.has_bank_account && isBankAccountLoading && (
                  <p className="mb-0 text-muted">Loading bank details...</p>
                )}

                {dashboard?.has_bank_account && !isBankAccountLoading && bankAccount && (
                  <div className="affiliate-bank-details">
                    <Row className="g-3">
                      <Col xs={12} sm={6}>
                        <div className="affiliate-bank-detail-item">
                          <span className="affiliate-bank-detail-label">Account Holder</span>
                          <span className="affiliate-bank-detail-value">
                            {formatDisplayValue(bankAccount.account_holder_name)}
                          </span>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="affiliate-bank-detail-item">
                          <span className="affiliate-bank-detail-label">Bank Name</span>
                          <span className="affiliate-bank-detail-value">
                            {formatDisplayValue(bankAccount.bank_name)}
                          </span>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="affiliate-bank-detail-item">
                          <span className="affiliate-bank-detail-label">Account Number</span>
                          <span className="affiliate-bank-detail-value">
                            {displayMaskedAccountNumber(bankAccount)}
                          </span>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="affiliate-bank-detail-item">
                          <span className="affiliate-bank-detail-label">Routing Number</span>
                          <span className="affiliate-bank-detail-value">
                            {formatDisplayValue(bankAccount.routing_number)}
                          </span>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="affiliate-bank-detail-item">
                          <span className="affiliate-bank-detail-label">Country</span>
                          <span className="affiliate-bank-detail-value">
                            {formatDisplayValue(bankAccount.country)}
                          </span>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="affiliate-bank-detail-item">
                          <span className="affiliate-bank-detail-label">Account Type</span>
                          <span className="affiliate-bank-detail-value text-capitalize">
                            {formatDisplayValue(bankAccount.account_type)}
                          </span>
                        </div>
                      </Col>
                      {bankAccount.iban && (
                        <Col xs={12} sm={6}>
                          <div className="affiliate-bank-detail-item">
                            <span className="affiliate-bank-detail-label">IBAN</span>
                            <span className="affiliate-bank-detail-value">
                              {formatDisplayValue(bankAccount.iban)}
                            </span>
                          </div>
                        </Col>
                      )}
                      {bankAccount.swift_code && (
                        <Col xs={12} sm={6}>
                          <div className="affiliate-bank-detail-item">
                            <span className="affiliate-bank-detail-label">SWIFT Code</span>
                            <span className="affiliate-bank-detail-value">
                              {formatDisplayValue(bankAccount.swift_code)}
                            </span>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <AffiliateBankAccountModal
        show={showBankModal}
        onHide={() => setShowBankModal(false)}
        bankAccount={bankAccount}
        hasBankAccount={!!dashboard?.has_bank_account}
        onSaved={handleBankSaved}
      />
    </section>
  );
};

export default AffiliateProgram;
